<div class="bill-list-container">
  <!-- Empty State -->
  @if (billService.processedBills().length === 0) {
    <div class="empty-state">
      <div class="empty-icon">
        <i class="material-symbols-rounded">description</i>
      </div>
      <p>No bills found.</p>
    </div>
  }

  <!-- Table -->
  @if (billService.processedBills().length > 0) {
    <div class="bill-table-wrapper">
      <table class="bill-table">
        <thead>
          <!-- Column Headers -->
          <tr class="column-header-row">
            <th class="checkbox-cell">
              <mat-checkbox
                (change)="toggleSelectAll()"
                [checked]="billService.selectedBillIds().length > 0 && billService.selectedBillIds().length === billService.processedBills().length"
                [indeterminate]="billService.selectedBillIds().length > 0 && billService.selectedBillIds().length < billService.processedBills().length"
              ></mat-checkbox>
            </th>
            <th class="pair-spine-header"></th>
            <th class="warning-header-cell"></th>
            @for (group of COLUMN_GROUPS; track group; let groupIndex = $index) {
              @for (key of group.columns; track key; let colIndex = $index) {
                @if (visibleColumns()[key]) {
                  @if (key === 'actions') {
                    <th class="actions-header-cell align-center">
                      <button
                        #headerGearBtn
                        type="button"
                        (click)="openColumnSelector.emit(headerGearBtn); $event.stopPropagation()"
                        class="column-settings-btn"
                        title="Columns"
                        aria-label="Column settings"
                        >
                        <i class="material-symbols-rounded">settings</i>
                      </button>
                    </th>
                  } @else {
                    <th
                      app-sortable-header
                      [label]="columnLabels[key] || ''"
                      [tooltip]="columnTooltips[key] || null"
                      [columnKey]="key"
                      [align]="['type', 'confidence', 'startDate', 'endDate'].includes(key) ? 'center' : 'left'"
                      [class.section-start]="isFirstVisibleInGroup(groupIndex, colIndex, group.columns)"
                      [class.align-center]="['type', 'confidence', 'startDate', 'endDate'].includes(key)"
                      [class.status-cell]="key === 'status'"
                    ></th>
                  }
                }
              }
            }
          </tr>
        </thead>
        <tbody>
          @for (entry of rowEntries(); track entry.key) {
            @if (entry.bill; as bill) {
            <tr
              [class.selected]="billService.selectedBillIds().includes(bill.id)"
              [class.pair-member]="entry.pairPos && !entry.rejected"
              [class.broken-pair]="entry.pairPos && entry.rejected"
              >
              <td class="checkbox-cell" (click)="$event.stopPropagation()">
                <mat-checkbox
                  [checked]="billService.selectedBillIds().includes(bill.id)"
                  (change)="billService.toggleSelection(bill.id, 'bill')"
                  [disabled]="bill.status === BillStatus.ERROR"
                ></mat-checkbox>
              </td>
              @if (entry.pairPos === 'first') {
                <td class="pair-spine-cell" rowspan="2">
                  <button
                    type="button"
                    class="pair-spine"
                    [class.broken]="entry.rejected"
                    [matTooltip]="entry.pairTooltip || ''"
                    matTooltipPosition="right"
                    matTooltipClass="bill-warning-tooltip"
                    (click)="billService.openPairedBillResolver(entry.group!); $event.stopPropagation()"
                    [attr.aria-label]="entry.rejected ? 'Unpaired bills' : 'Paired bills'"
                    >
                    <span class="pair-spine-bar"></span>
                    <span class="pair-spine-icon"><i class="material-symbols-rounded">{{ entry.rejected ? 'link_off' : 'link' }}</i></span>
                  </button>
                </td>
              } @else if (!entry.pairPos) {
                <td class="pair-spine-cell"></td>
              }
              @if (bill.status === BillStatus.ERROR) {
                <!-- Error row: message spans data columns, actions sticky right -->
                <td [attr.colspan]="errorColspan()" class="error-row-cell">
                  <div class="error-row-content">
                    <i class="material-symbols-rounded error-row-icon">error</i>
                    <span class="error-row-message">{{ bill.error || 'Processing error' }}</span>
                    <span class="error-row-filename">{{ bill.file.name }}</span>
                    @if (billRetriable()[bill.id]) {
                      <button (click)="onRetry(bill); $event.stopPropagation()" class="status-badge error" title="Click to retry">
                        <i class="material-symbols-rounded">refresh</i>
                        Retry
                      </button>
                    }
                  </div>
                </td>
                <td class="actions-cell">
                  <div class="actions-wrapper">
                    <button (click)="billService.deleteBill(bill.id); $event.stopPropagation()" class="action-btn delete" title="Delete">
                      <i class="material-symbols-rounded">delete</i>
                    </button>
                  </div>
                </td>
              } @else {
              <td class="warning-icon-cell">
                @if (bill.warnings?.length) {
                  <i class="material-symbols-rounded row-warning-icon"
                    [matTooltip]="billWarningText()[bill.id]"
                    matTooltipPosition="right"
                    matTooltipClass="bill-warning-tooltip">warning</i>
                }
              </td>
              @for (group of COLUMN_GROUPS; track group; let groupIndex = $index) {
                @for (key of group.columns; track key; let colIndex = $index) {
                  @if (visibleColumns()[key]) {
                    <td
                      [class.actions-cell]="key === 'actions'"
                      [class.section-start]="key !== 'actions' && isFirstVisibleInGroup(groupIndex, colIndex, group.columns)"
                      [class.align-center]="['type', 'confidence', 'startDate', 'endDate'].includes(key)"
                      [class.status-cell]="key === 'status'"
                    >
                      @switch (key) {
                        <!-- Status Column -->
                        @case ('status') {
                          @if (bill.status === BillStatus.PENDING) {
                            <span class="status-badge pending">
                              Pending
                            </span>
                          }
                          @if (bill.status === BillStatus.PROCESSING) {
                            <span class="status-badge processing">
                              <span class="processing-dots">
                                <span></span><span></span><span></span>
                              </span>
                              Processing
                            </span>
                          }
                          @if (bill.status === BillStatus.REVIEW_NEEDED || bill.status === BillStatus.APPROVED) {
                            <span class="status-badge processed">
                              <i class="material-symbols-rounded">check_circle</i> Processed
                            </span>
                          }
                          @if (bill.status === BillStatus.DUPLICATE_DETECTED) {
                            <button (click)="billService.openDuplicateResolver(bill); $event.stopPropagation()" class="status-badge duplicate">
                              <i class="material-symbols-rounded">warning</i> Duplicate
                            </button>
                          }
                        }
                        <!-- Type Column -->
                        @case ('type') {
                          <app-bill-type-badge [type]="getBillType(bill.data)" [commodity]="bill.commodityType ?? bill.data?.commodityType"></app-bill-type-badge>
                        }
                        <!-- Provider Column -->
                        @case ('provider') {
                          <span class="cell-text primary">{{ bill.data?.providerName || '-' }}</span>
                        }
                        <!-- Utility Column -->
                        @case ('utility') {
                          <span class="cell-text truncate" [title]="bill.data?.utilityName || ''">{{ getUtilityDisplay(bill.data) }}</span>
                        }
                        <!-- Legal Entity Column -->
                        @case ('legalEntity') {
                          <span class="cell-text truncate truncate-lg semibold" [title]="bill.data?.legalEntity || ''">{{ bill.data?.legalEntity || '-' }}</span>
                        }
                        <!-- File Name Column -->
                        @case ('fileName') {
                          <div class="file-cell" [title]="bill.file.name">
                            <i class="material-symbols-rounded">description</i>
                            <span>{{ bill.file.name }}</span>
                            @if (bill.periodCount && bill.periodCount > 1) {
                              <span class="cell-text">Period {{ bill.periodIndex }} of {{ bill.periodCount }}</span>
                            }
                          </div>
                        }
                        <!-- Amount Column -->
                        @case ('amount') {
                          <span class="cell-text bold">{{ bill.data?.totalAmount | currency:bill.data?.currency }}</span>
                        }
                        <!-- Confidence Column -->
                        @case ('confidence') {
                          <div class="confidence-cell">
                            <i class="material-symbols-rounded icon-fill"
                               [class.good]="(bill.data?.confidence || 0) > 0.75"
                               [class.bad]="(bill.data?.confidence || 0) > 0.5 && (bill.data?.confidence || 0) <= 0.75"
                               [class.ugly]="(bill.data?.confidence || 0) <= 0.5">
                               fiber_manual_record
                            </i>
                            <span>{{ ((bill.data?.confidence || 0) * 100) | number:'1.0-0' }}%</span>
                          </div>
                        }
                          <!-- Actions Column -->
                        @case ('actions') {
                          <div class="actions-wrapper">
                            <button
                              (click)="billService.previewBill.set(bill); $event.stopPropagation()"
                              class="action-btn view"
                              title="View bill"
                              >
                              <i class="material-symbols-rounded">description</i>
                            </button>
                            <button
                              (click)="billService.deleteBill(bill.id); $event.stopPropagation()"
                              class="action-btn delete"
                              title="Delete"
                              >
                              <i class="material-symbols-rounded">delete</i>
                            </button>
                          </div>
                        }
                        <!-- Default Column -->
                        @default {
                          <span class="cell-text">
                            {{ getBillDataValue(bill, key) }}
                          </span>
                        }
                      }
                    </td>
                  }
                }
              }
              }
            </tr>
            }
          }
        </tbody>
      </table>
    </div>
  }
</div>
